import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { Switch } from './ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Label } from './ui/label';
import { toast } from 'sonner';
import { Sparkles, RefreshCw, ChevronDown, ChevronUp, Settings2 } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const TIME_OPTIONS = ['05:00', '05:30', '06:00', '06:30', '07:00', '07:30', '08:00', '08:30', '09:00', '09:30', '10:00'];

const renderMarkdown = (text) => {
  return (text || '').split('\n').map((line, i) => {
    if (line.startsWith('## ')) return <h3 key={i} className="text-sm font-bold uppercase tracking-wide text-primary mt-3 mb-1">{line.slice(3)}</h3>;
    if (line.startsWith('# ')) return <h3 key={i} className="text-sm font-bold mt-3 mb-1">{line.slice(2)}</h3>;
    const bolded = line.split(/(\*\*[^*]+\*\*)/g).map((part, j) =>
      part.startsWith('**') && part.endsWith('**') ? <b key={j}>{part.slice(2, -2)}</b> : part);
    if (/^\s*[-*]\s/.test(line)) return <p key={i} className="text-sm pl-4 relative before:content-['•'] before:absolute before:left-1">{bolded && line.replace(/^\s*[-*]\s/, '')}</p>;
    if (/^\s*\d+\.\s/.test(line)) return <p key={i} className="text-sm pl-4">{bolded}</p>;
    if (!line.trim()) return <div key={i} className="h-1" />;
    return <p key={i} className="text-sm">{bolded}</p>;
  });
};

const BriefingSettings = () => {
  const [prefs, setPrefs] = useState(null);
  const [openPop, setOpenPop] = useState(false);

  useEffect(() => {
    if (!openPop || prefs) return;
    axios.get(`${API}/ai/briefing/preferences`)
      .then(res => setPrefs(res.data))
      .catch(() => toast.error('Could not load briefing preferences'));
  }, [openPop, prefs]);

  const save = async (next) => {
    setPrefs(next);
    try {
      await axios.put(`${API}/ai/briefing/preferences`, next);
      toast.success('Briefing preferences saved');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save preferences');
    }
  };

  return (
    <Popover open={openPop} onOpenChange={setOpenPop}>
      <PopoverTrigger asChild>
        <Button size="sm" variant="ghost" className="h-8 w-8 p-0" data-testid="briefing-settings-btn">
          <Settings2 className="h-4 w-4" />
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-72 space-y-4" data-testid="briefing-settings-popover">
        <p className="text-sm font-semibold">Briefing Delivery</p>
        {prefs ? (
          <>
            <div className="flex items-center justify-between">
              <Label htmlFor="briefing-email-toggle" className="text-sm">Email me the briefing</Label>
              <Switch id="briefing-email-toggle" checked={prefs.email_enabled}
                onCheckedChange={(v) => save({ ...prefs, email_enabled: v })} data-testid="briefing-email-toggle" />
            </div>
            <div className="flex items-center justify-between gap-3">
              <Label className="text-sm">Delivery time</Label>
              <Select value={prefs.briefing_time} onValueChange={(v) => save({ ...prefs, briefing_time: v })}
                disabled={!prefs.email_enabled}>
                <SelectTrigger className="w-28 h-8" data-testid="briefing-time-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {TIME_OPTIONS.map(t => <SelectItem key={t} value={t} data-testid={`briefing-time-${t}`}>{t}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <p className="text-xs text-muted-foreground">Your briefing is emailed each morning at the selected time (SAST).</p>
          </>
        ) : (
          <p className="text-xs text-muted-foreground">Loading…</p>
        )}
      </PopoverContent>
    </Popover>
  );
};

export const ExecBriefing = () => {
  const [briefing, setBriefing] = useState(null);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(true);

  const load = useCallback(async (refresh = false) => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/ai/briefing`, { params: { refresh } });
      setBriefing(res.data);
    } catch (e) {
      if (refresh) toast.error(e.response?.data?.detail || 'Briefing generation failed');
      setBriefing(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // Load cached briefing only (does not auto-generate to save credits)
    (async () => {
      try {
        const res = await axios.get(`${API}/ai/briefing`);
        setBriefing(res.data);
      } catch (e) { /* no cached briefing yet */ }
    })();
  }, []);

  return (
    <Card className="!p-0 border-primary/20 bg-gradient-to-br from-primary/[0.03] to-transparent" data-testid="exec-briefing-card">
      <CardHeader className="pb-2 flex-row items-center justify-between space-y-0">
        <CardTitle className="text-base font-semibold flex items-center gap-2">
          <Sparkles className="h-4 w-4 text-primary" /> Executive Morning Briefing
          {briefing && <span className="text-xs font-normal text-muted-foreground">({briefing.date})</span>}
        </CardTitle>
        <div className="flex items-center gap-1">
          <BriefingSettings />
          <Button size="sm" variant="outline" className="h-8 gap-1" disabled={loading} onClick={() => load(true)} data-testid="briefing-generate-btn">
            <RefreshCw className={`h-3.5 w-3.5 ${loading ? 'animate-spin' : ''}`} />
            {loading ? 'Generating…' : briefing ? 'Regenerate' : 'Generate Briefing'}
          </Button>
          {briefing && (
            <Button size="sm" variant="ghost" className="h-8" onClick={() => setOpen(!open)} data-testid="briefing-toggle-btn">
              {open ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
            </Button>
          )}
        </div>
      </CardHeader>
      {open && (
        <CardContent data-testid="briefing-content">
          {briefing ? (
            <div className="space-y-0.5">{renderMarkdown(briefing.content)}</div>
          ) : (
            <p className="text-sm text-muted-foreground">
              No briefing generated today. Click <b>Generate Briefing</b> for an AI summary of overnight changes, exceptions and pending decisions — based only on your live data.
            </p>
          )}
        </CardContent>
      )}
    </Card>
  );
};
